import { CurrencyPipe } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Apollo, QueryRef } from 'apollo-angular';
import { LicenceTypeId, PaymentSubscription } from '../../shared/entities/payment/payment';
import { TenantLicenceRequest } from '../../shared/entities/tenantLicenceRequest/tenantLicenceRequest';
import { TenantLicenceStatus } from '../../shared/entities/tenantLicenceStatus/TenantLicenceStatus';
import { LoadingService } from '../../shared/services/loading.service';
import { PaymentsService } from '../../shared/services/payment/payment.service';
import { UsersService } from '../../shared/services/users/users.service';
import * as fromGraphQl from './graphql';

import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { interval, Subscription } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { Tenant } from '../../shared/entities/tenant/tenant';
import { User } from '../../shared/entities/user/user';
import { TenantsService } from '../../shared/services/tenants/tenants.service';
import { ToastService } from '../../shared/services/toast/toast.service';
import { ConfirmDialogEventType, ConfirmDialogSeverity } from '../confirmDialog/confirmDialogComponent';
import { ConfirmDialogService } from '../confirmDialog/confirmDialogService';
import { GrowthBookService } from '../../shared/services/growthbook.service';
import { RolesService } from '../../shared/services/roles/roles.service';

@Component({
  selector: 'app-billing-licence-view',
  templateUrl: './billing-licence-view.component.html',
  styleUrls: ['./billing-licence-view.component.scss']
})
export class BillingLicenceViewComponent implements OnInit, OnDestroy {

  dialogRef: DynamicDialogRef | undefined;

  public queryRef: QueryRef<fromGraphQl.SubscriptionQueryResponse>;
  public querySubscription: Subscription;
  tenantLicenceStatuses: TenantLicenceStatus[] = [];
  public subscription: PaymentSubscription;
  public quantities: any;
  tenantLicenceRequests: TenantLicenceRequest[] = [];
  public tenant: Tenant;
  loading = true;
  paymentIntegration = true;
  
  constructor(
    public loadingService: LoadingService,
    public paymentService: PaymentsService,
    public currencyPipe: CurrencyPipe,
    private apollo: Apollo,
    private userService: UsersService,
    private confirmDialogService: ConfirmDialogService,
    private tenantService: TenantsService,
    private toastService: ToastService,
    @Inject('env') private environment : any,
    private growthBookService: GrowthBookService,
    private rolesService: RolesService,
  ) {

  }

  ngOnInit(): void {
    this.subscribeToLicenceData();
  }

  subscribeToLicenceData() {
    this.queryRef = this.apollo.watchQuery<fromGraphQl.SubscriptionQueryResponse>({
      fetchPolicy: 'network-only',
      query: fromGraphQl.SUBSCRIPTION_QUERY,
      pollInterval: 10000,
      variables: {
        tenantId: this.userService.getCurrentTenantId(),
      },
    });
    this.querySubscription = this.queryRef.valueChanges.subscribe( (result) => {
      this.loading = false;
      if (result?.data?.subscription) {
        this.subscription = result?.data?.subscription;
        if(this.subscription?.state?.toString() === "NO_PAYMENT"){
          this.paymentIntegration = false;
        }
        // console.log('subscription', this.subscription);
        let count = result?.data?.subscription?.items?.length;
        this.quantities = Array(count).fill(0);
        this.quantities?.forEach((quantity: any, index: number) => {
          this.quantities[index] = this.subscription?.items[index]?.quantity;
        });
      }
      if (result?.data?.tenantLicenceRequests) {
        this.tenantLicenceRequests = TenantLicenceRequest.createTenantLicenceRequests(result?.data?.tenantLicenceRequests);
      }
      if(result?.data?.tenantLicenceRequestsStatuses){
        this.tenantLicenceStatuses = TenantLicenceStatus.createTenantLicenceStatuses(result?.data?.tenantLicenceRequestsStatuses);
      }
      if(result?.data?.tenant){
        this.tenant = new Tenant(result?.data?.tenant);
      }
    });
  }


  ngOnDestroy(): void {
    this.querySubscription?.unsubscribe();
  }

  isIncreaseProcessing(): boolean {
    return this.tenantLicenceStatuses?.find((status) => status?.isIncreaseProcessing) ? true : false;
  }

  isDecreaseProcessing(): boolean {
    return this.tenantLicenceStatuses?.find((status) => status?.isDecreaseProcessing) ? true : false;
  }

  deleteLicenceRequests() {
    const message = 'Möchtest Du die vorgemerkte Lizenzanpassung wirklich löschen?';
    this.dialogRef = this.confirmDialogService.open({
      message: message,
      severity: ConfirmDialogSeverity.WARN,
      header: 'Lizenzanpassung löschen'
    });
    this.dialogRef.onClose.subscribe( (result : ConfirmDialogEventType) => {
      if(result === ConfirmDialogEventType.CONFIRM){
        this.loading = true;
        this.tenantService?.deleteLicenceRequests(this.tenant?.id, () => {
          interval(400)
            .pipe(
              switchMap(() => this.apollo.query<fromGraphQl.DecreaseDeletionStatusQueryResponse>({
                query: fromGraphQl.DECREASE_DELETION_STATUS_QUERY,
                  variables: {
                    tenantId: this.tenant?.id
                  }
                })),
                filter((result) => {
                  return result?.data?.tenantLicenceRequestsStatuses.length == 0;
                }),
                take(1)
              ).subscribe(() => {
                this.queryRef.refetch();
                this.toastService.addToastWithMessage('Vorgemerkte Lizenzanpassung gelöscht.');
              });
          });
      };
    })
  }

  isTrial(): boolean {
    return this.subscription?.state?.toString() === 'TRIAL';
  }
  
  convertToEuro(amount: any) {
    return this.paymentService?.getCurrency(amount, "eur", this.currencyPipe);
  }

  getItemTotal(item: any, index: number): any {
    return item.price * this.quantities[index];
  }

  getAmountItemTotal(item: any): any {
    return item.price * item.quantity;
  }

  getAmountItemsTotal() {
    let total = 0;
    for (let i = 0; i < this.subscription?.amountItems.length; i++) {
      let item = this.subscription?.amountItems[i];
      total = total + this.getAmountItemTotal(item);
    }
    return total - this.getAmountItemsTotalDiscountValue();
  }

  getAmountItemsTotalDiscountValue(): number {
    let total = 0;
    for (let i = 0; i < this.subscription?.amountItems.length; i++) {
      let item = this.subscription?.amountItems[i];
      total = total + this.getAmountItemDiscountValue(item, i);
    }
    return total;
  }

  getAmountItemDiscountValue(item: any, index: number): number {
    let discountValue = 0;
    let itemTotal = this.getAmountItemTotal(item);
    if (item?.discount?.percentOff) {
      discountValue = (Number(item?.discount.percentOff) * itemTotal /100);
    }
    else if (item?.discount?.amountOff) {
      discountValue = Number(item?.discount.amountOff);
    }
    // prevent negative values
    if(itemTotal - discountValue < 0){
      return 0;
    }
    return discountValue;
  }

  getInterval(item: any) {
    return this.paymentService?.getInterval(item?.interval);
  }

  getItemDiscount(item: any): string {
    if (item?.discount?.percentOff) {
      return item.discount.percentOff + '%';
    }
    else if (item?.discount?.amountOff) {
      return this.convertToEuro(item.discount?.amountOff);
    }
    return '-';
  }

  countUsedAmount(amountItems: any[]): number {
    let total = 0;
    amountItems?.forEach((item: any) => {
      if (item?.totalAmountUsed) {
        total = total + Number(item?.totalAmountUsed);
      }
    });
    return total;
  }

  getItemDiscountValue(item: any, index: number): number {
    let discountValue = 0;
    let itemTotal = this.getItemTotal(item, index);
    if (item?.discount?.percentOff) {
      discountValue = (Number(item?.discount.percentOff) * itemTotal /100);
    }
    else if (item?.discount?.amountOff) {
      discountValue = Number(item?.discount.amountOff);
    }
    // prevent negative values
    if(itemTotal - discountValue < 0){
      return 0;
    }
    return discountValue;
  }

  getSubscriptionDiscount(): any {
    if (this.subscription?.discount?.percentOff) {
      return this.subscription.discount.percentOff + '%';
    }
    else if (this.subscription?.discount?.amountOff) {
      return this.convertToEuro(this.subscription.discount?.amountOff);
    }
    return '-';
  }

  getSubscriptionDiscountValue(): number {
    let total = this.getTotal();
    let discountValue = 0;
    if (this.subscription?.discount?.percentOff) {
      discountValue = (Number(this.subscription?.discount.percentOff) * total /100);
    }
    else if (this.subscription?.discount?.amountOff) {
      discountValue = Number(this.subscription?.discount.amountOff);
    }
    // prevent negative values
    if(total - discountValue < 0){
      return 0;
    }
    return discountValue;
  }

  getTotal() {
    let total = 0;
    for (let i = 0; i < this.subscription?.items.length; i++) {
      let item = this.subscription?.items[i];
      total = total + this.getItemTotal(item, i);
    }
    return total - this.getTotalDiscountValue();
  }

  getTotalDiscountValue(): number {
    let total = 0;
    for (let i = 0; i < this.subscription?.items.length; i++) {
      let item = this.subscription?.items[i];
      total = total + this.getItemDiscountValue(item, i);
    }
    return total;
  }

  getTotalWithDiscount() {
    return this.getTotal() - this.getSubscriptionDiscountValue();
  }

  isDecreasing(): boolean {
    let isDecreasing = false;
    this.quantities?.forEach((quantity: any, index: number) => {
      if (quantity < this.subscription?.items[index]?.quantity) {
        isDecreasing = true;
      }
    });
    return isDecreasing;
  }

  update() {
    // this.error = '';
    this.loading = true;
    let input: { tenantId: string; actionType: string; licenceType: LicenceTypeId; licenceCount: number; }[] = [];
    this.quantities?.forEach((quantity: any, index: number) => {
      if (quantity !== this.subscription?.items[index]?.quantity) {
        let type = quantity > this.subscription?.items[index]?.quantity? 'ADD': 'REMOVE';
        input.push({
          tenantId:  this.tenant?.id,
          actionType: type,
          licenceType: this.subscription?.items[index].type,
          licenceCount:  Math.abs(quantity - +this.subscription?.items[index]?.quantity)
        })
      }
    });
    try{
      this.paymentService.createLicence(
        input,
        (result: any) => {
          this.loading = false;
          // this.loadingService.setLoading(false);
          if (result.error) {
            for( let error of result.error){
              if (error?.extensions?.isUpstreamError) {
                // this.error = error?.message;
                // let dialogRef = this.dialog.open(this.ErrorDialog,{autoFocus: false});
                // dialogRef?.afterClosed().subscribe((dialogResult: any) => {
                //   window?.location?.reload();
                // });
                console.log('error', error);
              }
            }
            this.queryRef.refetch();
          }
          else {
            this.queryRef.refetch();
          }
        });
    }
    catch(error){
      this.loading = false;
      console.log('error', error);
    }
  }

  checkChanged(): boolean{
    let changed = false;
    this.quantities?.forEach((quantity: any, index: number) => {
      if (quantity !== this.subscription?.items[index]?.quantity) {
       changed = true;
      }
    });
    return changed;
  }

  getRemainig(licence: TenantLicenceRequest): string {
    if (this.subscription?.items) {
      const quantity = this.subscription?.items?.find((item: any) => item.type === licence?.licenceType)?.quantity;
      const remaining = Number(quantity) - Number(licence.licenceCount);
      return 'von '+ quantity + ' auf ' + remaining;
    }
    else {
      return '-';
    }
  }

  onCounterChange(count: number, index: any) {
    this.quantities[index] = count;
  }

  getLicenceType(licenceType: string){
    return User.getLicenceType(licenceType)
  }

  clear(){
    // reload page
    window.location.reload();
  }

  isAmoutBasedPricingActive(): boolean {
    return this.growthBookService.isFeatureEnabled('multiLanguage');;
  }

  isAdmin(): boolean {
    return this.rolesService.hasRole('framework_tenants_manage');
  }

  recalculateLicences() {
    const message = 'Möchtest du die zugewiesenen Lizenzen neu berechnen?';
    this.dialogRef = this.confirmDialogService.open({
      message: message,
      severity: ConfirmDialogSeverity.DEFAULT,
      header: 'Lizenzen neu berechnen'
    });
    this.dialogRef.onClose.subscribe( (result : ConfirmDialogEventType) => {
      if(result === ConfirmDialogEventType.CONFIRM){
        this.loadingService.setLoading(true);
        this.paymentService.recalculateLicences(this.tenant?.id, (result: any) => {
          this.loadingService.setLoading(false);
          this.toastService.addToastWithMessage('Lizenzen wurden neu berechnet.');
        });
      };
    })
      // do nothing
  };
  
}
